// PatientsList.tsx
import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Space, message, Pagination } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import moment from 'moment';
import axios from 'axios';
import { Patient, Visit } from '../components/type';
import SearchFilters from './SearchFilters';
import { usePatientContext } from './PatientContext';
import API from '../config/api';
import { useLanguage } from './LanguageContext';
import { ExperimentOutlined, WarningOutlined } from '@ant-design/icons';

// Extended visit interface with patient information
interface VisitWithPatientInfo {
  _id: string;
  visit_id: string;
  date: string;
  time: string;
  visit_type: string;
  visit_urgency?: string; // Add urgency field
  complaint: string;
  diagnosis: string;
  receipts: any[];
  patient_name: string;
  patient_phone: string;
  patient_id: string;
  file_number?: string;
  age?: string;
  address?: string;
  externalServiceRequestCount?: number;
  externalServiceRequests?: Array<{
    service_name: string;
    provider_name: string;
    status: string;
    requestedAt: Date;
  }>;
  payment_type?: string;
  insurance_company_name?: string;
  _original_patient_id: string; // To reference back to original patient
}

interface PatientsListProps {
  refreshTrigger?: number; // Add prop to receive refresh triggers from parent
}

const PatientsList: React.FC<PatientsListProps> = ({ refreshTrigger = 0 }) => {
  const {
    patients,
    filteredPatients,
    setSelectedPatient,
    setFilteredPatients,
    fetchPatients,
    pagination: contextPagination
  } = usePatientContext();

  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [availableVisitTypes, setAvailableVisitTypes] = useState<any[]>([]);

  useEffect(() => {
    const doctorId = localStorage.getItem('doctorId');
    if (doctorId) {
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`)
        .then(res => { if (res.data.success) setAvailableVisitTypes(res.data.data.visit_types || []); })
        .catch(() => {});
    }
  }, []);

  const resolveVisitTypeName = (rawValue: string): string => {
    if (!rawValue) return '';
    const byId = availableVisitTypes.find((t: any) => t.type_id === rawValue);
    if (byId) return byId.name || rawValue;
    const byNameAr = availableVisitTypes.find((t: any) => t.name_ar === rawValue);
    if (byNameAr) return byNameAr.name || rawValue;
    const byName = availableVisitTypes.find((t: any) => t.name === rawValue);
    if (byName) return byName.name || rawValue;
    return rawValue;
  };
  const [visitsToDisplay, setVisitsToDisplay] = useState<VisitWithPatientInfo[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());

  // Extract and flatten all visits from patients
  // Reversed sort function - newest first
  // Backend already handles date filtering, so we trust the API response
  // Helper: parse a raw date value (string, Date, or number) to YYYY-MM-DD string.
  // Returns '' when the value is absent or cannot be parsed.
  const parseDateValue = (raw: any): string => {
    if (!raw) return '';
    if (typeof raw === 'string') {
      return raw.includes('T') ? raw.split('T')[0] : raw;
    }
    if (raw instanceof Date) return raw.toISOString().split('T')[0];
    const str = String(raw);
    return str.includes('T') ? str.split('T')[0] : str;
  };

  const extractVisits = (patientData: Patient[]): VisitWithPatientInfo[] => {
    const allVisits: VisitWithPatientInfo[] = [];

    patientData.forEach(patient => {
      // Build a pseudo-visit entry once and reuse it as fallback
      const pseudoVisit: VisitWithPatientInfo = {
        _id: `generated-${patient.patient_id}`,
        visit_id: '',
        date: parseDateValue((patient as any).date) || '',
        time: (patient as any).time || '',
        visit_type: patient.visit_type || '',
        visit_urgency: (patient as any).visit_urgency || 'normal',
        complaint: '',
        diagnosis: '',
        receipts: [],
        patient_name: patient.patient_name || '',
        patient_phone: patient.patient_phone || '',
        patient_id: patient.patient_id || '',
        file_number: (patient as any).file_number || '',
        age: patient.age || '',
        address: patient.address || '',
        externalServiceRequestCount: (patient as any).externalServiceRequestCount || 0,
        externalServiceRequests: (patient as any).externalServiceRequests || [],
        payment_type: (patient as any).payment_type || 'cash',
        insurance_company_name: (patient as any).insurance_company_name || '',
        _original_patient_id: patient.patient_id || ''
      };

      if (patient.visits && patient.visits.length > 0) {
        let latestVisit: Visit | null = null;
        let latestTimestamp = Number.NEGATIVE_INFINITY;

        patient.visits.forEach(visit => {
          // Parse visit date; fall back to patient.date when visit.date is missing
          let visitDateStr = parseDateValue((visit as any).date);
          if (!visitDateStr) {
            visitDateStr = parseDateValue((patient as any).date);
          }

          if (!visitDateStr) return; // No date at all — skip this visit

          const visitMoment = moment(visitDateStr, ['YYYY-MM-DD', 'YYYY-M-D', 'YYYY/MM/DD', 'YYYY/M/D', moment.ISO_8601]).startOf('day');
          if (!visitMoment.isValid()) return; // Unparseable date — skip

          const timestamp = parseDateAndTime(visitDateStr, visit.time || '');
          if (timestamp >= latestTimestamp) {
            latestVisit = visit;
            latestTimestamp = timestamp;
          }
        });

        // If every visit in the array had an invalid/missing date, still show the patient
        if (!latestVisit) {
          allVisits.push(pseudoVisit);
        } else {
          const latest: Visit = latestVisit;
          allVisits.push({
            _id: (latest as any)._id || `latest-${patient.patient_id}`,
            visit_id: latest.visit_id || '',
            date: parseDateValue((latest as any).date) || parseDateValue((patient as any).date),
            time: latest.time || '',
            visit_type: latest.visit_type || patient.visit_type || '',
            visit_urgency: (patient as any).visit_urgency || 'normal',
            complaint: latest.complaint || '',
            diagnosis: latest.diagnosis || '',
            receipts: typeof latest.receipts === 'string' ? [] : (latest.receipts || []),
            patient_name: patient.patient_name || '',
            patient_phone: patient.patient_phone || '',
            patient_id: patient.patient_id || '',
            file_number: (patient as any).file_number || '',
            age: patient.age || '',
            address: patient.address || '',
            externalServiceRequestCount: (patient as any).externalServiceRequestCount || 0,
            externalServiceRequests: (patient as any).externalServiceRequests || [],
            payment_type: (patient as any).payment_type || 'cash',
            insurance_company_name: (patient as any).insurance_company_name || '',
            _original_patient_id: patient.patient_id || ''
          });
        }
      } else {
        // No visits array — show a single row for the patient using their registration info
        allVisits.push(pseudoVisit);
      }
    });

    // Sort visits by date and time (newest first) - this ensures recent visits appear at the top
    return allVisits.sort((a, b) => {
      // Parse dates with times to get accurate sorting
      const dateTimeA = parseDateAndTime(a.date, a.time);
      const dateTimeB = parseDateAndTime(b.date, b.time);

      // Sort newest first (descending order)
      return dateTimeB - dateTimeA;
    });
  };

  // Helper function to convert Arabic numerals to regular numerals
  const convertArabicNumerals = (str: string): string => {
    if (!str) return str;
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    const regularNumerals = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let converted = str;
    arabicNumerals.forEach((arabic, index) => {
      converted = converted.replace(new RegExp(arabic, 'g'), regularNumerals[index]);
    });
    return converted;
  };

  // Helper function to properly parse dates with times (handles Arabic numerals)
  const parseDateAndTime = (dateStr: string, timeStr: string): number => {
    if (!dateStr) return 0;

    // Convert Arabic numerals to regular numerals
    const convertedDateStr = convertArabicNumerals(dateStr);
    const convertedTimeStr = convertArabicNumerals(timeStr || '');

    // First try to parse with time
    if (convertedTimeStr) {
      const fullDateStr = `${convertedDateStr} ${convertedTimeStr}`;
      const momentDate = moment(fullDateStr, [
        'YYYY-MM-DD HH:mm',
        'YYYY-M-D HH:mm',
        'YYYY-MM-DD H:mm',
        'YYYY-M-D H:mm',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-M-D HH:mm:ss'
      ], true); // Strict mode

      if (momentDate.isValid()) {
        return momentDate.valueOf();
      }
    }

    // Fallback to just date
    const momentDate = moment(convertedDateStr, [
      'YYYY-MM-DD',
      'YYYY-M-D'
    ], true); // Strict mode

    return momentDate.isValid() ? momentDate.valueOf() : 0;
  };

  // Fetch visits data (actually fetching patients and extracting visits)
  const fetchVisits = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      console.log('Fetching patients data for PatientsList component with pagination:', { page, pageSize });

      // Use fetchPatients from context with pagination params for server-side pagination.
      // Sort by the patient's latest visit date so bulk-import timestamps do not
      // replay the source-file row order.
      const result = await fetchPatients({
        page,
        limit: pageSize,
        sortBy: 'date',
        sortOrder: 'desc'
      });

      // Update timestamp of last refresh
      setLastRefreshTime(Date.now());

      // Get the latest patients data from context
      const patientsData = result.patients;

      // Extract visits for display
      const visits = extractVisits(patientsData);
      setVisitsToDisplay(visits);

      if (result.pagination) {
        setPagination({
          current: result.pagination.currentPage,
          pageSize: result.pagination.itemsPerPage,
          total: result.pagination.totalItems
        });
      } else {
        setPagination({
          current: page,
          pageSize,
          total: visits.length
        });
      }
    } catch (error) {
      console.error('Error fetching visits data:', error);
      message.error('Failed to refresh patients data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Listen for refresh triggers from parent component
  useEffect(() => {
    if (refreshTrigger > 0) {
      const currentTime = Date.now();
      // Implement debouncing - only refresh if more than 2 seconds since last refresh
      if (currentTime - lastRefreshTime > 2000) {
        console.log('PatientsList refreshing due to trigger:', refreshTrigger);
        fetchVisits(pagination.current, pagination.pageSize);
      } else {
        console.log('Ignoring refresh trigger - too soon since last refresh');
      }
    }
  }, [refreshTrigger, lastRefreshTime]);

  // The server already returns the requested patient page.
  const getPaginatedVisits = () => {
    return visitsToDisplay;
  };

  // Update visits when filtered patients change
  useEffect(() => {
    if (filteredPatients) {
      if (filteredPatients.length > 0) {
        setVisitsToDisplay(extractVisits(filteredPatients));
      } else {
        // Clear visits when no filtered patients (e.g., search with no results)
        setVisitsToDisplay([]);
        // Also reset pagination
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    }
  }, [filteredPatients]);

  // On first login, PatientContext refetches after the doctor/clinic assignment
  // finishes resolving. Keep this component's separate pagination state aligned
  // with that later, authoritative response.
  useEffect(() => {
    if (!contextPagination) return;
    setPagination({
      current: contextPagination.currentPage,
      pageSize: contextPagination.itemsPerPage,
      total: contextPagination.totalItems
    });
  }, [contextPagination]);

  // Define columns for the visits table
  const columns = [
    {
      title: t('patient'),
      key: 'patient',
      render: (record: VisitWithPatientInfo) => (
        <Space direction="vertical" size="small">
          <Space>
            <Typography.Text strong>{record.patient_name}</Typography.Text>
            {record.visit_urgency === 'urgent' && (
              <Tag color="red" icon={<WarningOutlined />}>
                عاجل
              </Tag>
            )}
            {record.payment_type === 'insurance' ? (
              <Tag color="green" style={{ fontSize: 11 }}>
                تعاقد {record.insurance_company_name ? `— ${record.insurance_company_name}` : ''}
              </Tag>
            ) : (
              <Tag color="default" style={{ fontSize: 11 }}>نقدي</Tag>
            )}
          </Space>
          <Typography.Text type="secondary">{record.patient_phone}</Typography.Text>
          {record.file_number && (
            <Tag color="gold" style={{ fontSize: 11 }}>رقم ملف: {record.file_number}</Tag>
          )}
        </Space>
      )
    },
    {
      title: t('visitDate'),
      dataIndex: 'date',
      key: 'date',
      render: (text: string) => (
        <span>{text ? moment(text).format('YYYY-MM-DD') : 'N/A'}</span>
      )
    },
    {
      title: t('time'),
      dataIndex: 'time',
      key: 'time',
      render: (timeStr: string) => {
        if (!timeStr) return 'N/A';

        // Convert Arabic numerals to regular numerals
        const convertedTime = convertArabicNumerals(timeStr);

        // Parse 24-hour time and convert to 12-hour format
        try {
          // Handle formats like "14:35" or "02:35" or "2:35"
          const timeMatch = convertedTime.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const hours = parseInt(timeMatch[1], 10);
            const minutes = timeMatch[2];
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            return `${displayHours}:${minutes} ${period}`;
          }
          // If format doesn't match, return as is
          return timeStr;
        } catch (error) {
          return timeStr;
        }
      },
    },
    {
      title: t('externalServicesCount'),
      key: 'externalServices',
      render: (record: VisitWithPatientInfo) => {
        const count = record.externalServiceRequestCount || 0;
        const services = record.externalServiceRequests || [];

        if (count === 0) {
          return <span style={{ color: '#ccc' }}>-</span>;
        }

        // Show each service as a separate tag with color based on status
        return (
          <Space size={4} wrap>
            {services.map((service: any, index: number) => (
              <Tag
                key={index}
                icon={<ExperimentOutlined />}
                color={service.status === 'completed' ? 'green' : 'orange'}
                style={{ cursor: 'help', marginBottom: 4 }}
                title={`${service.service_name} - ${service.provider_name} (${service.status})`}
              >
                {service.service_name}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: t('age'),
      dataIndex: 'age',
      key: 'age',
    },
    {
      title: t('visitType'),
      key: 'visit_type',
      render: (record: VisitWithPatientInfo) => (
        <Space direction="vertical" size={2}>
          <Tag color="blue">{resolveVisitTypeName(record.visit_type) || 'Regular'}</Tag>
          {record.visit_urgency === 'urgent' && (
            <Tag color="volcano" style={{ fontSize: '11px' }}>Urgent</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('receipts'),
      key: 'receipts',
      render: (record: VisitWithPatientInfo) => {
        const count = record.receipts?.length || 0;
        return count > 0 ?
          <Tag color="green">{count} receipt(s)</Tag> :
          <Tag color="gray">No receipts</Tag>;
      }
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (text: string, record: VisitWithPatientInfo) => {
        // Find the original patient
        const patient = patients.find(p => p.patient_id === record._original_patient_id);

        return (
          <a
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click from triggering
              if (patient) {
                setSelectedPatient(patient);
              }
            }}
          >
            {t('viewPatient')}
          </a>
        );
      },
    }
  ];


  return (
    <>
      <style>
        {`
          .urgent-patient-row {
            background-color: #fff1f0 !important;
          }
          .urgent-patient-row:hover {
            background-color: #ffe7e7 !important;
          }
          .urgent-patient-row td {
            background-color: #fff1f0 !important;
          }
        `}
      </style>
      <SearchFilters />

      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        {/* pagination={false} prevents AntD from slicing dataSource internally.
            Server already returns one page of rows; a separate Pagination component
            below drives page changes without any client-side slicing. */}
        <Table
          columns={columns}
          dataSource={getPaginatedVisits()}
          rowKey={record => `${record.patient_id || ''}-${record.visit_id || ''}-${record.date || ''}-${record.time || ''}`}
          loading={loading}
          locale={{
            emptyText: visitsToDisplay.length === 0 && !loading ? t('noVisits') : undefined
          }}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) => record.visit_urgency === 'urgent' ? 'urgent-patient-row' : ''}
          onRow={(record) => ({
            onClick: () => {
              const patient = patients.find(p => p.patient_id === record._original_patient_id);
              if (patient) {
                setSelectedPatient(patient);
              }
            },
            style: { cursor: 'pointer' }
          })}
        />

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={contextPagination ? pagination.total : visitsToDisplay.length}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            showTotal={(total, range) => {
              if (total === 0) return t('noVisits');
              return `Showing ${range[0]}-${range[1]} of ${total} visits`;
            }}
            onChange={(page, size) => {
              fetchVisits(page, size || pagination.pageSize);
            }}
            onShowSizeChange={(current, size) => {
              fetchVisits(1, size);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default PatientsList;
